/** Iter 232 · PIP Status card for the "My performance" tab.
 *
 *  When the user is NOT on a PIP, renders a subtle-but-informative placeholder
 *  listing the eligibility signals the workspace admin has enabled (or, if
 *  none are enabled yet, an even lighter "no formal triggers configured" note).
 *  When they ARE on an active PIP, renders the plan: title, dates, goals,
 *  checkpoints timeline, and current outcome trajectory.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import PIPWeeklyPulse from "@/components/PIPWeeklyPulse";
import { ShieldAlert, ShieldCheck, Info, Target, Calendar, Circle, CircleDot, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

const ratingColor = {
  green: "text-emerald-600 border-emerald-300 bg-emerald-50",
  amber: "text-amber-700 border-amber-300 bg-amber-50",
  red:   "text-rose-700 border-rose-300 bg-rose-50",
};

const outcomeCopy = {
  passed:   { text: "Passed", cls: "text-emerald-600" },
  failed:   { text: "Not passed", cls: "text-rose-600" },
  extended: { text: "Extended", cls: "text-amber-600" },
};

export default function PIPStatusCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/me/pip")
      .then((r) => setData(r.data))
      .catch(() => setData({ active: null, config: null }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="border border-border p-6 text-sm text-muted-foreground" data-testid="pip-card-loading">
        Loading performance status…
      </div>
    );
  }

  const plan = data?.active;
  if (plan) return <PIPActiveCard plan={plan} />;
  return <PIPPlaceholderCard config={data?.config} />;
}

// ─────────────────────────────── Placeholder ───────────────────────────────

function PIPPlaceholderCard({ config }) {
  const rules = config?.rules || [];
  const enabled = rules.filter((r) => r.enabled);
  const meta = config?.signal_meta || {};

  return (
    <div className="border border-border p-6 space-y-4" data-testid="pip-placeholder-card">
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-10 h-10 border border-emerald-300 bg-emerald-50 flex items-center justify-center">
          <ShieldCheck className="w-5 h-5 text-emerald-600" />
        </div>
        <div className="flex-1">
          <div className="text-base font-semibold" style={{ fontFamily: "var(--font-heading)" }}>
            You are not on a Performance Improvement Plan
          </div>
          <div className="text-sm text-muted-foreground mt-1">
            Keep it that way — the signals below are what your workspace admin watches. Staying on top of them keeps things easy.
          </div>
        </div>
      </div>

      <div className="border-t border-border pt-4">
        <div className="label-eyebrow mb-2">Signals that could trigger a PIP</div>
        {rules.length === 0 ? (
          <p className="text-sm text-muted-foreground italic border border-dashed border-border p-3">
            Your workspace hasn&apos;t configured any formal PIP triggers yet. Consistent delivery and clear communication is enough for now.
          </p>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="pip-signal-list">
            {rules.map((r) => {
              const m = meta[r.key] || { label: r.key, unit: "", hint: "" };
              const on = r.enabled;
              return (
                <li key={r.key}
                    className={`border p-3 flex gap-3 items-start ${on ? "border-primary/40 bg-primary/5" : "border-border bg-muted/20 opacity-70"}`}
                    data-testid={`pip-signal-${r.key}`}>
                  {on
                    ? <ShieldAlert className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    : <Info className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />}
                  <div className="flex-1">
                    <div className="text-sm font-medium">{m.label}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">{m.hint}</div>
                    <div className={`text-[11px] font-mono mt-1 ${on ? "text-primary font-bold" : "text-muted-foreground"}`}>
                      Threshold: {r.threshold} {m.unit}
                    </div>
                    {!on && (
                      <div className="text-[10px] text-muted-foreground italic mt-1">Not currently enforced.</div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="border-t border-border pt-3 text-[11px] text-muted-foreground">
        This is a company-wide framework — the same rules apply to everyone equally. If you have concerns about any signal, raise them early with your manager.
      </div>
    </div>
  );
}

// ─────────────────────────────── Active plan ───────────────────────────────

function PIPActiveCard({ plan }) {
  const cps = plan.checkpoints || [];
  const nextCp = cps.find((c) => !c.completed);
  const latestRated = [...cps].reverse().find((c) => c.rating);
  const daysRemaining = plan.end_date
    ? Math.max(0, Math.ceil((new Date(plan.end_date) - new Date()) / 86400000))
    : null;

  return (
    <div className="border-2 border-amber-400 bg-amber-50/40 p-6 space-y-4" data-testid="pip-active-card">
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-10 h-10 border border-amber-500 bg-amber-100 flex items-center justify-center">
          <ShieldAlert className="w-5 h-5 text-amber-700" />
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-wider text-amber-700 font-bold">Active PIP</div>
          <div className="text-lg font-bold" style={{ fontFamily: "var(--font-heading)" }} data-testid="pip-active-title">{plan.title}</div>
          <div className="text-sm text-muted-foreground mt-0.5">Initiated by {plan.initiator_name || "your manager"}.</div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Days remaining</div>
          <div className={`text-2xl font-bold tabular-nums ${daysRemaining !== null && daysRemaining < 7 ? "text-rose-600" : "text-amber-700"}`} data-testid="pip-days-remaining">
            {daysRemaining ?? "—"}
          </div>
        </div>
      </div>

      <div className="border-t border-amber-300 pt-3">
        <div className="label-eyebrow mb-2">Why</div>
        <p className="text-sm text-foreground whitespace-pre-wrap" data-testid="pip-reason">{plan.reason_summary}</p>
      </div>

      {(plan.goals || []).length > 0 && (
        <div className="border-t border-amber-300 pt-3">
          <div className="label-eyebrow mb-2">Improvement goals</div>
          <ul className="space-y-1" data-testid="pip-goals">
            {plan.goals.map((g, i) => (
              <li key={i} className="text-sm flex items-start gap-2">
                <Target className="w-3.5 h-3.5 mt-0.5 text-primary shrink-0" />
                <span>{g}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="border-t border-amber-300 pt-3">
        <div className="label-eyebrow mb-2">Checkpoints</div>
        {cps.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">No checkpoints scheduled yet.</p>
        ) : (
          <ol className="space-y-2" data-testid="pip-checkpoints">
            {cps.map((c) => {
              const Icon = c.completed ? CheckCircle2 : c === nextCp ? CircleDot : Circle;
              const iconCls = c.completed ? "text-emerald-500" : c === nextCp ? "text-primary" : "text-muted-foreground";
              return (
                <li key={c.id} className="flex items-start gap-2 text-sm" data-testid={`pip-checkpoint-${c.id}`}>
                  <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${iconCls}`} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{c.focus}</span>
                      <span className="text-[10px] text-muted-foreground font-mono">
                        <Calendar className="w-2.5 h-2.5 inline -mt-0.5 mr-0.5" />
                        {c.scheduled_at}
                      </span>
                      {c.rating && (
                        <span className={`text-[10px] px-1.5 py-0.5 border font-bold uppercase ${ratingColor[c.rating]}`}>
                          {c.rating}
                        </span>
                      )}
                    </div>
                    {c.notes && <div className="text-xs text-muted-foreground mt-1">{c.notes}</div>}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>

      <div className="border-t border-amber-300 pt-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-muted-foreground">
          <Calendar className="w-3 h-3 inline -mt-0.5 mr-1" />
          <span className="font-mono">{plan.start_date} → {plan.end_date}</span>
          {plan.consequence_on_fail && (
            <span className="ml-2">· Consequence if not passed: <span className="font-semibold">{plan.consequence_on_fail.replace(/_/g, " ")}</span></span>
          )}
        </div>
        {latestRated && (
          <div className={`text-xs px-2 py-1 border ${ratingColor[latestRated.rating]}`}>
            Latest review: <span className="font-bold uppercase">{latestRated.rating}</span>
          </div>
        )}
      </div>

      <div className="border-t border-amber-300 pt-3 text-[11px] text-muted-foreground">
        <AlertTriangle className="w-3 h-3 inline -mt-0.5 mr-1 text-amber-600" />
        A PIP is a formal HR process. Meet each checkpoint, deliver on the goals, and communicate any blockers to your manager promptly.
      </div>

      <PIPWeeklyPulse planId={plan.id} />
    </div>
  );
}
